import React from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import Seo, { faqJsonLd, breadcrumbJsonLd } from "@/components/Seo";
import { Breadcrumbs, FaqBlock, CtaBanner, RelatedLinks } from "@/pages/seo/SeoLanding";
import NotFoundPage from "@/pages/NotFound";
import { BERUFE } from "@/data/berufe";
import { SITUATIONEN } from "@/data/situationen";
import { Check, Sparkles, Quote } from "lucide-react";

function Section({ title, children }) {
    return (
        <div className="mt-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{title}</h2>
            <div className="mt-4">{children}</div>
        </div>
    );
}

const Chips = ({ items, tone = "slate" }) => (
    <div className="flex flex-wrap gap-2">
        {items.map((s) => (
            <span key={s} className={`px-3.5 py-1.5 rounded-full text-sm font-medium border ${tone === "red" ? "bg-red-50 text-red-700 border-red-100" : "bg-white text-slate-700 border-slate-200"}`}>{s}</span>
        ))}
    </div>
);

export default function BerufPage({ variant }) {
    const { slug } = useParams();
    const beruf = BERUFE.find((b) => b.slug === slug);
    const situation = !beruf ? SITUATIONEN.find((s) => s.slug === slug) : null;
    if (!beruf && !situation) return <NotFoundPage />;

    const isCv = variant === "lebenslauf";
    const d = beruf || situation;
    const name = d.name;
    const basePath = `/${variant}/${slug}`;
    const title = beruf
        ? isCv
            ? `Lebenslauf ${name} – Muster, Vorlage & Tipps | Bewerbung24.ai`
            : `Bewerbung als ${name} – Muster, Anschreiben & Tipps | Bewerbung24.ai`
        : isCv
            ? `Lebenslauf ${name} – so überzeugst du | Bewerbung24.ai`
            : `Bewerbung ${name} – Tipps, Formulierungen & Muster | Bewerbung24.ai`;
    const metaDescription = beruf
        ? isCv
            ? `Lebenslauf als ${name} erstellen: Aufbau, wichtige Skills, Formulierungen und Beispiele. Mit KI in Minuten fertig — ATS-optimiert und kostenlos starten.`
            : `Bewerbung als ${name} schreiben: Anschreiben-Formulierungen, wichtige Kompetenzen und Tipps. Mit KI in 60 Sekunden erstellen — kostenlos starten.`
        : `Bewerbung ${name}: Strategien, Formulierungen und Lebenslauf-Tipps für deine Situation. Mit KI überzeugend formulieren — kostenlos starten.`;

    const crumbs = [
        { name: "Start", path: "/" },
        { name: isCv ? "Lebenslauf" : "Bewerbung", path: isCv ? "/lebenslauf-erstellen" : "/bewerbung-erstellen" },
        { name, path: basePath },
    ];
    const faq = d.faq || [];
    const jsonLd = [breadcrumbJsonLd(crumbs)];
    if (faq.length) jsonLd.push(faqJsonLd(faq));

    const related = [
        { name: isCv ? `Bewerbung ${beruf ? "als " : ""}${name}` : `Lebenslauf ${name}`, path: `/${isCv ? "bewerbung" : "lebenslauf"}/${slug}` },
        ...(beruf
            ? BERUFE.filter((b) => b.kategorie === beruf.kategorie && b.slug !== slug).slice(0, 3).map((b) => ({ name: `${isCv ? "Lebenslauf" : "Bewerbung"} ${b.name}`, path: `/${variant}/${b.slug}` }))
            : SITUATIONEN.filter((s) => s.slug !== slug).slice(0, 3).map((s) => ({ name: `Bewerbung ${s.name}`, path: `/bewerbung/${s.slug}` }))),
        { name: "Alle Berufe", path: "/berufe" },
        { name: "KI-Bewerbung", path: "/ki-bewerbung" },
        { name: "ATS-Check", path: "/ats-check" },
        { name: "Bewerbungsfoto mit KI", path: "/bewerbungsfoto" },
    ];

    return (
        <PublicLayout>
            <Seo title={title} description={metaDescription} path={basePath} jsonLd={jsonLd} />
            <section className="max-w-4xl mx-auto px-6 lg:px-8 py-14" data-testid={`beruf-page-${variant}-${slug}`}>
                <Breadcrumbs items={crumbs} />
                <p className="mt-6 text-[11px] font-bold tracking-[0.2em] uppercase text-red-600">{beruf ? beruf.kategorie : "Bewerbungssituation"}</p>
                <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 mt-2 leading-tight">
                    {beruf ? (isCv ? `Lebenslauf als ${name}` : `Bewerbung als ${name}`) : (isCv ? `Lebenslauf ${name}` : `Bewerbung ${name}`)}
                </h1>
                <p className="mt-4 text-slate-600 leading-relaxed">{d.intro}</p>
                {beruf?.gehalt && (
                    <p className="mt-3 text-sm text-slate-500">Übliches Gehalt: <strong className="text-slate-700">{beruf.gehalt}</strong></p>
                )}
                <div className="mt-7 flex flex-wrap gap-3">
                    <Link to="/registrieren" data-testid="beruf-cta" className="px-7 py-3.5 rounded-full font-bold text-sm btn-primary-grad inline-flex items-center gap-2">
                        <Sparkles size={15} /> {isCv ? "Lebenslauf" : "Bewerbung"} mit KI erstellen — kostenlos
                    </Link>
                </div>

                {beruf ? (
                    <>
                        <Section title={`Typische Aufgaben als ${name}`}>
                            <ul className="space-y-2">
                                {beruf.aufgaben.map((a) => (
                                    <li key={a} className="flex items-start gap-2 text-slate-700 text-sm sm:text-base"><Check size={16} className="text-emerald-600 mt-1 shrink-0" /> {a}</li>
                                ))}
                            </ul>
                        </Section>
                        <Section title={isCv ? "Diese Skills gehören in deinen Lebenslauf" : "Diese Kompetenzen überzeugen im Anschreiben"}>
                            <p className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Fachliche Kenntnisse</p>
                            <Chips items={beruf.hardSkills} tone="red" />
                            <p className="text-sm font-bold text-slate-500 uppercase tracking-wider mt-5 mb-2">Soft Skills</p>
                            <Chips items={beruf.softSkills} />
                        </Section>
                        {!isCv && (
                            <Section title="Formulierungen für dein Anschreiben">
                                <div className="space-y-3">
                                    {beruf.anschreiben.map((s, i) => (
                                        <div key={i} className="card-elev p-5 border-l-4 border-amber-400 flex gap-3">
                                            <Quote size={16} className="text-amber-500 shrink-0 mt-0.5" />
                                            <p className="text-sm text-slate-700 italic leading-relaxed">{s}</p>
                                        </div>
                                    ))}
                                </div>
                                <p className="text-xs text-slate-500 mt-3">Tipp: Die KI von Bewerbung24.ai formuliert solche Sätze individuell aus deinem Werdegang und der konkreten Stellenanzeige — ohne Floskeln, ohne erfundene Fakten.</p>
                            </Section>
                        )}
                        {isCv && (
                            <Section title={`Lebenslauf-Tipps für ${name}`}>
                                <ul className="space-y-2.5">
                                    {beruf.cvTipps.map((t, i) => (
                                        <li key={i} className="flex items-start gap-2 text-slate-700 text-sm sm:text-base"><Check size={16} className="text-emerald-600 mt-1 shrink-0" /> {t}</li>
                                    ))}
                                </ul>
                            </Section>
                        )}
                        {beruf.ausbildung && (
                            <Section title="Ausbildung & Voraussetzungen">
                                <p className="text-slate-700 text-sm sm:text-base leading-relaxed">{beruf.ausbildung}</p>
                            </Section>
                        )}
                    </>
                ) : (
                    <>
                        <Section title="Die größten Herausforderungen">
                            <ul className="space-y-2">
                                {situation.herausforderungen.map((h) => (
                                    <li key={h} className="flex items-start gap-2 text-slate-700 text-sm sm:text-base"><Check size={16} className="text-amber-500 mt-1 shrink-0" /> {h}</li>
                                ))}
                            </ul>
                        </Section>
                        <Section title="Die richtige Strategie">
                            <ul className="space-y-2.5">
                                {situation.strategien.map((s) => (
                                    <li key={s} className="flex items-start gap-2 text-slate-700 text-sm sm:text-base"><Check size={16} className="text-emerald-600 mt-1 shrink-0" /> {s}</li>
                                ))}
                            </ul>
                        </Section>
                        <Section title="So formulierst du es im Anschreiben">
                            <div className="space-y-3">
                                {situation.anschreibenTipps.map((s, i) => (
                                    <div key={i} className="card-elev p-5 border-l-4 border-amber-400 flex gap-3">
                                        <Quote size={16} className="text-amber-500 shrink-0 mt-0.5" />
                                        <p className="text-sm text-slate-700 italic leading-relaxed">{s}</p>
                                    </div>
                                ))}
                            </div>
                        </Section>
                        <Section title="Tipps für deinen Lebenslauf">
                            <ul className="space-y-2.5">
                                {situation.cvTipps.map((t, i) => (
                                    <li key={i} className="flex items-start gap-2 text-slate-700 text-sm sm:text-base"><Check size={16} className="text-emerald-600 mt-1 shrink-0" /> {t}</li>
                                ))}
                            </ul>
                        </Section>
                    </>
                )}

                <FaqBlock faq={faq} />
                <CtaBanner label={`Jetzt ${isCv ? "Lebenslauf" : "Bewerbung"} mit KI erstellen`} />
                <RelatedLinks links={related} />
            </section>
        </PublicLayout>
    );
}
